<template>
    <div :class="['listener-playing', showLyricsPanel ? 'is-lyrics-mode' : '']" v-if="displayTrack">
        <div class="listener-playing__topbar">
            <button class="listener-playing__icon-button" @click="goBack">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M15 18 9 12l6-6"></path>
                </svg>
            </button>
            <div class="listener-playing__heading">
                <div class="listener-playing__eyebrow">{{ $tt('正在播放') }}</div>
                <div class="listener-playing__caption">{{ displayTrack.album || displayTrack.artist }}</div>
            </div>
            <button class="listener-playing__icon-button" @click="toggleFavourite">
                <svg viewBox="0 0 24 24" :fill="displayTrack.favourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                    <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                </svg>
            </button>
        </div>

        <div class="listener-playing__display-switch">
            <button
                :class="['listener-playing__display-pill', !showLyricsPanel ? 'is-active' : '']"
                @click="setDisplayMode(false)"
            >
                {{ $tt('封面') }}
            </button>
            <button
                :class="['listener-playing__display-pill', showLyricsPanel ? 'is-active' : '']"
                @click="setDisplayMode(true)"
            >
                {{ $tt('歌词') }}
            </button>
        </div>

        <div v-if="!showLyricsPanel" class="listener-playing__art-stage">
            <div :class="['listener-playing__vinyl', isPlaying ? 'is-spinning' : '']"></div>
            <div class="listener-playing__art-card">
                <img
                    :src="displayTrack.image || '/static/dist/img/album_null.jpg'"
                    :alt="displayTrack.title"
                    class="listener-playing__art-image"
                >
            </div>
        </div>

        <div v-else class="listener-playing__lyrics-stage">
            <div class="listener-playing__lyrics-fade listener-playing__lyrics-fade--top"></div>
            <div class="listener-playing__lyrics-fade listener-playing__lyrics-fade--bottom"></div>
            <div ref="lyricContainer" class="listener-playing__lyrics-scroll">
                <div class="listener-playing__lyrics-spacer"></div>
                <button
                    v-for="(line, index) in lyricsLines"
                    :key="line.uid"
                    :data-lyric-index="index"
                    :class="lyricLineClass(index)"
                    @click="seekToLyric(line)"
                >
                    {{ line.lyric }}
                </button>
                <div v-if="!lyricsLines.length" class="listener-playing__lyrics-empty">
                    {{ $tt('暂无歌词') }}
                </div>
                <div class="listener-playing__lyrics-spacer"></div>
            </div>
        </div>

        <div class="listener-playing__meta">
            <h1 class="listener-playing__title">{{ displayTrack.title }}</h1>
            <p class="listener-playing__artist">{{ displayTrack.artist || displayTrack.album }}</p>
        </div>

        <div class="listener-playing__progress-block">
            <div
                ref="progressCurve"
                class="listener-playing__progress-curve"
                role="slider"
                tabindex="0"
                aria-label="播放进度"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="Math.round(activeProgressValue * 100)"
                @mousedown.prevent="startProgressDrag"
                @touchstart.prevent="startProgressTouchDrag"
                @keydown.prevent="handleProgressKeydown"
            >
                <svg
                    class="listener-playing__progress-svg"
                    viewBox="0 0 320 56"
                    preserveAspectRatio="none"
                >
                    <path
                        ref="progressCurvePath"
                        class="listener-playing__progress-track"
                        :d="progressCurvePath"
                    ></path>
                    <path
                        class="listener-playing__progress-fill"
                        :d="progressCurvePath"
                        :style="progressCurveStyle"
                    ></path>
                    <circle
                        class="listener-playing__progress-thumb"
                        :cx="progressThumbPoint.x"
                        :cy="progressThumbPoint.y"
                        r="4.75"
                    ></circle>
                </svg>
            </div>
            <div class="listener-playing__time">
                <span>{{ formatDuration(currentTime) }}</span>
                <span>{{ formatDuration(duration) }}</span>
            </div>
        </div>

        <div class="listener-playing__toolbar">
            <button
                class="listener-playing__toolbar-button"
                :class="repeat ? 'is-active' : ''"
                @click="toggleRepeat"
            >
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 7h11v3l4-4-4-4v3H5v6h2V7zm10 10H6v-3l-4 4 4 4v-3h13v-6h-2v4z"></path>
                </svg>
            </button>
            <button class="listener-playing__toolbar-button" @click="previous">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 12 21 18V6l-10 6zM3 18l7-6-7-6v12z"></path>
                </svg>
            </button>
            <button class="listener-playing__play-button" @click="togglePlay">
                <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 5h3v14H7zm7 0h3v14h-3z"></path>
                </svg>
            </button>
            <button class="listener-playing__toolbar-button" @click="next">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 6v12l10-6L3 6zm11 0v12l7-6-7-6z"></path>
                </svg>
            </button>
            <button class="listener-playing__toolbar-button" @click="openQueue">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 6h14v2H4zm0 5h14v2H4zm0 5h14v2H4zm16 0h2V6h-2z"></path>
                </svg>
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'ListenerPlayingDetails',
    data() {
        return {
            displayTrack: null,
            lyricsLines: [],
            currentLyricIndex: -1,
            showLyricsPanel: false,
            progressCurveLength: 0,
            progressDragValue: null,
            progressCurvePath: 'M12 30 C 58 14, 110 46, 160 30 S 262 14, 308 30'
        }
    },
    computed: {
        ...mapGetters(['track', 'trackId', 'currentTime', 'duration', 'repeat']),
        isPlaying() {
            return this.$store.state.common.isPlaying
        },
        queue() {
            return this.$store.state.common.queue || []
        },
        queueIndex() {
            return this.$store.state.common.queueIndex
        },
        progressValue() {
            if (!this.duration || this.duration <= 0) return 0
            return Math.min(this.currentTime / this.duration, 1)
        },
        activeProgressValue() {
            return this.progressDragValue === null ? this.progressValue : this.progressDragValue
        },
        progressCurveStyle() {
            const totalLength = this.progressCurveLength || 1
            const filledLength = totalLength * this.activeProgressValue
            return {
                strokeDasharray: `${filledLength} ${totalLength}`
            }
        },
        progressThumbPoint() {
            const path = this.$refs.progressCurvePath
            const totalLength = this.progressCurveLength
            if (!path || !totalLength) {
                return { x: 12, y: 30 }
            }
            const point = path.getPointAtLength(totalLength * this.activeProgressValue)
            return { x: point.x, y: point.y }
        }
    },
    watch: {
        track: {
            immediate: true,
            handler(value) {
                this.syncDisplayTrack(value)
            }
        },
        trackId(value) {
            if (value && String(this.$route.params.id) !== String(value)) {
                this.$router.replace({
                    name: 'playingList',
                    params: { id: value },
                    query: {
                        ...this.$route.query,
                        view: this.showLyricsPanel ? 'lyrics' : 'cover'
                    }
                })
            }
        },
        '$route.query.view': {
            immediate: true,
            handler(value) {
                this.showLyricsPanel = value === 'lyrics'
            }
        },
        currentTime(value) {
            this.syncCurrentLyric(value)
        },
        '$route.params.id': {
            immediate: true,
            handler() {
                this.ensureTrackData()
            }
        },
        displayTrack() {
            this.refreshProgressCurveMetrics()
        }
    },
    mounted() {
        this.refreshProgressCurveMetrics()
    },
    beforeDestroy() {
        this.detachProgressDragListeners()
    },
    methods: {
        async ensureTrackData() {
            if (this.track && String(this.track.id) === String(this.$route.params.id)) {
                await this.syncDisplayTrack(this.track)
                return
            }
            const song = await this.$api.subsonic.getSong(this.$route.params.id)
            const track = {
                ...song,
                favourite: !!song.favourite
            }
            this.displayTrack = track
            await this.fetchLyrics(track.id)
        },
        async syncDisplayTrack(track) {
            if (!track) return
            this.displayTrack = {
                ...track,
                favourite: !!track.favourite
            }
            await this.fetchLyrics(track.id)
        },
        formatDuration(value) {
            const safeValue = Number(value) || 0
            const minutes = Math.floor(safeValue / 60)
            const seconds = Math.floor(safeValue % 60)
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        },
        async fetchLyrics(trackId) {
            this.lyricsLines = []
            this.currentLyricIndex = -1
            if (!trackId) return
            try {
                const res = await this.$api.subsonic.getLyrics(trackId)
                this.lyricsLines = this.formatLyrics(res.value)
                this.syncCurrentLyric(this.currentTime)
            } catch (error) {
                this.lyricsLines = []
                this.currentLyricIndex = -1
            }
        },
        formatLyrics(lyrics) {
            if (!lyrics) return []
            const regNewLine = /\n/
            const lineArr = lyrics.split(regNewLine)
            const lyricsObjArr = []
            lineArr.forEach(item => {
                if (item === '') return
                const spiltList = item.split(']')
                const spiltListLength = spiltList.length
                const content = spiltList[spiltListLength - 1].trim()
                const timeArray = spiltList.slice(0, spiltListLength - 1)
                timeArray.forEach(dt => {
                    const obj = {}
                    obj.lyric = content === '' ? '' : content
                    obj.time = this.formatLyricTime(dt.slice(1))
                    obj.uid = `${obj.time}-${Math.random().toString().slice(-6)}`
                    if (obj.lyric !== '') {
                        lyricsObjArr.push(obj)
                    }
                })
            })
            return lyricsObjArr.sort((a, b) => a.time - b.time)
        },
        formatLyricTime(item) {
            if (item.indexOf('.') === -1) {
                const timeParts = item.split(':')
                const hours = parseInt(timeParts[0], 10)
                const minutes = parseInt(timeParts[1], 10)
                const seconds = parseInt(timeParts[2], 10)
                return Number(hours * 60 * 60 + minutes * 60 + seconds)
            }
            const regTime = /\d{2}:\d{2}.\d{2,3}/
            const isTime = item.match(regTime)
            const time = isTime ? isTime[0] : '00:00.00'
            const regMin = /.*:/
            const regSec = /:.*\./
            const regMs = /\./
            const min = parseInt(time.match(regMin)[0].slice(0, 2), 10)
            let sec = parseInt(time.match(regSec)[0].slice(1, 3), 10)
            const ms = time.slice(time.match(regMs).index + 1, time.match(regMs).index + 3)
            if (min !== 0) {
                sec += min * 60
            }
            return Number(`${sec}.${ms}`)
        },
        syncCurrentLyric(value) {
            if (!this.lyricsLines.length) {
                this.currentLyricIndex = -1
                return
            }
            const current = Number(value) || 0
            let activeIndex = -1
            for (let i = 0; i < this.lyricsLines.length; i += 1) {
                if (current >= parseFloat(this.lyricsLines[i].time)) {
                    activeIndex = i
                } else {
                    break
                }
            }
            if (activeIndex !== this.currentLyricIndex) {
                this.currentLyricIndex = activeIndex
                this.scrollActiveLyricIntoView(activeIndex)
            }
        },
        scrollActiveLyricIntoView(index) {
            if (index < 0) return
            this.$nextTick(() => {
                const container = this.$refs.lyricContainer
                if (!container) return
                const target = container.querySelector(`[data-lyric-index="${index}"]`)
                if (!target) return
                const targetCenter = target.offsetTop + target.offsetHeight / 2
                const desiredTop = targetCenter - container.clientHeight / 2
                const maxTop = Math.max(0, container.scrollHeight - container.clientHeight)
                container.scrollTo({
                    top: Math.max(0, Math.min(desiredTop, maxTop)),
                    behavior: 'smooth'
                })
            })
        },
        lyricLineClass(index) {
            const distance = Math.abs(index - this.currentLyricIndex)
            return [
                'listener-playing__lyric-line',
                distance === 0 ? 'is-active' : '',
                distance === 1 ? 'is-near' : '',
                distance === 2 ? 'is-mid' : '',
                distance >= 3 ? 'is-far' : '',
            ]
        },
        setDisplayMode(isLyrics) {
            this.showLyricsPanel = isLyrics
            this.$router.replace({
                name: this.$route.name,
                params: this.$route.params,
                query: {
                    ...this.$route.query,
                    view: isLyrics ? 'lyrics' : 'cover'
                }
            }).catch(() => {})
        },
        seekToLyric(line) {
            if (!line || !this.duration) return
            const seek = Math.min((parseFloat(line.time) || 0) / this.duration, 1)
            this.$store.dispatch('seek', seek)
        },
        refreshProgressCurveMetrics() {
            this.$nextTick(() => {
                const path = this.$refs.progressCurvePath
                if (!path || typeof path.getTotalLength !== 'function') return
                this.progressCurveLength = path.getTotalLength()
            })
        },
        seekToProgress(value) {
            const safeValue = Math.max(0, Math.min(Number(value) || 0, 1))
            this.$store.dispatch('seek', safeValue)
        },
        startProgressDrag(event) {
            this.beginProgressDrag(event)
        },
        startProgressTouchDrag(event) {
            const touch = event.touches && event.touches[0]
            this.beginProgressDrag(touch)
        },
        beginProgressDrag(pointer) {
            const progress = this.resolveProgressFromPointer(pointer)
            if (progress === null) return
            this.progressDragValue = progress
            this.seekToProgress(progress)
            this.attachProgressDragListeners()
        },
        attachProgressDragListeners() {
            this.detachProgressDragListeners()
            window.addEventListener('mousemove', this.handleProgressDragMove)
            window.addEventListener('mouseup', this.stopProgressDrag)
            window.addEventListener('touchmove', this.handleProgressTouchMove, { passive: false })
            window.addEventListener('touchend', this.stopProgressDrag)
            window.addEventListener('touchcancel', this.stopProgressDrag)
        },
        detachProgressDragListeners() {
            window.removeEventListener('mousemove', this.handleProgressDragMove)
            window.removeEventListener('mouseup', this.stopProgressDrag)
            window.removeEventListener('touchmove', this.handleProgressTouchMove)
            window.removeEventListener('touchend', this.stopProgressDrag)
            window.removeEventListener('touchcancel', this.stopProgressDrag)
        },
        handleProgressDragMove(event) {
            this.updateProgressFromPointer(event)
        },
        handleProgressTouchMove(event) {
            if (event.cancelable) {
                event.preventDefault()
            }
            const touch = event.touches && event.touches[0]
            this.updateProgressFromPointer(touch)
        },
        updateProgressFromPointer(pointer) {
            const progress = this.resolveProgressFromPointer(pointer)
            if (progress === null) return
            this.progressDragValue = progress
            this.seekToProgress(progress)
        },
        stopProgressDrag() {
            this.detachProgressDragListeners()
            this.progressDragValue = null
        },
        handleProgressKeydown(event) {
            if (!this.duration) return
            if (event.key === 'Home') {
                this.seekToProgress(0)
                return
            }
            if (event.key === 'End') {
                this.seekToProgress(1)
                return
            }
            const deltaMap = {
                ArrowLeft: -0.02,
                ArrowDown: -0.02,
                ArrowRight: 0.02,
                ArrowUp: 0.02
            }
            const delta = deltaMap[event.key]
            if (!delta) return
            this.seekToProgress(this.progressValue + delta)
        },
        resolveProgressFromPointer(pointer) {
            const container = this.$refs.progressCurve
            if (!pointer || !container) return null
            const rect = container.getBoundingClientRect()
            if (!rect.width || !rect.height) return null
            const x = ((pointer.clientX - rect.left) / rect.width) * 320
            const y = ((pointer.clientY - rect.top) / rect.height) * 56
            return this.findNearestProgressOnCurve(x, y)
        },
        findNearestProgressOnCurve(x, y) {
            const path = this.$refs.progressCurvePath
            const totalLength = this.progressCurveLength
            if (!path || !totalLength) return 0
            let bestLength = 0
            let bestDistance = Number.POSITIVE_INFINITY
            const coarseSamples = 80
            for (let i = 0; i <= coarseSamples; i += 1) {
                const sampleLength = (i / coarseSamples) * totalLength
                const point = path.getPointAtLength(sampleLength)
                const distance = ((point.x - x) ** 2) + ((point.y - y) ** 2)
                if (distance < bestDistance) {
                    bestDistance = distance
                    bestLength = sampleLength
                }
            }
            let minLength = Math.max(0, bestLength - (totalLength / coarseSamples))
            let maxLength = Math.min(totalLength, bestLength + (totalLength / coarseSamples))
            for (let round = 0; round < 3; round += 1) {
                let refinedBestLength = bestLength
                let refinedBestDistance = bestDistance
                const refinedSamples = 12
                for (let i = 0; i <= refinedSamples; i += 1) {
                    const sampleLength = minLength + (((maxLength - minLength) * i) / refinedSamples)
                    const point = path.getPointAtLength(sampleLength)
                    const distance = ((point.x - x) ** 2) + ((point.y - y) ** 2)
                    if (distance < refinedBestDistance) {
                        refinedBestDistance = distance
                        refinedBestLength = sampleLength
                    }
                }
                bestLength = refinedBestLength
                bestDistance = refinedBestDistance
                const range = Math.max(totalLength / (coarseSamples * ((round + 2) * 2)), 1)
                minLength = Math.max(0, bestLength - range)
                maxLength = Math.min(totalLength, bestLength + range)
            }
            return bestLength / totalLength
        },
        togglePlay() {
            this.$store.dispatch('playPause')
        },
        next() {
            this.$store.dispatch('next')
        },
        previous() {
            this.$store.dispatch('previous')
        },
        toggleRepeat() {
            this.$store.dispatch('toggleRepeat')
        },
        openQueue() {
            this.$router.push({ name: 'playing-local' })
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.go(-1)
                return
            }
            this.$router.push({ path: '/discover' })
        },
        toggleFavourite() {
            if (!this.displayTrack) return
            const action = this.displayTrack.favourite
                ? this.$api.subsonic.removeFavourite(this.displayTrack.id, 'track')
                : this.$api.subsonic.addFavourite(this.displayTrack.id, 'track')
            action.then(() => {
                this.displayTrack.favourite = !this.displayTrack.favourite
                if (this.track && String(this.track.id) === String(this.displayTrack.id)) {
                    this.track.favourite = this.displayTrack.favourite
                }
            })
        }
    }
}
</script>

<style scoped>
.listener-playing {
    --display-stage-size: min(18rem, 74vw, 34dvh);
    height: 100%;
    min-height: 0;
    box-sizing: border-box;
    padding: 0.75rem 0.9rem 0.25rem;
    display: flex;
    flex-direction: column;
    color: #111827;
    overflow: hidden;
}

.listener-playing.is-lyrics-mode {
    padding-bottom: 0.5rem;
}

.listener-playing__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.listener-playing__heading {
    flex: 1;
    text-align: center;
    min-width: 0;
}

.listener-playing__eyebrow {
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-playing__caption {
    margin-top: 0.12rem;
    font-size: 0.84rem;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-playing__icon-button {
    width: 2.8rem;
    height: 2.8rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.84);
    color: #111827;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.16);
    flex-shrink: 0;
}

.listener-playing__icon-button svg {
    width: 1.2rem;
    height: 1.2rem;
}

.listener-playing__display-switch {
    margin: 1.15rem auto 0;
    padding: 0.28rem;
    width: fit-content;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
    display: inline-grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.28rem;
}

.listener-playing__display-pill {
    min-width: 4.7rem;
    height: 2.2rem;
    padding: 0 1rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 700;
}

.listener-playing__display-pill.is-active {
    background: #111827;
    color: #fff;
}

.listener-playing__art-stage {
    position: relative;
    margin: 1.25rem auto 1.35rem;
    width: var(--display-stage-size);
    height: var(--display-stage-size);
    flex-shrink: 0;
}

.listener-playing__vinyl {
    position: absolute;
    right: -1.5rem;
    top: 1.7rem;
    width: 72%;
    height: 72%;
    border-radius: 999px;
    background:
        radial-gradient(circle at center, #0f172a 0 10%, #111827 10% 15%, #020617 15% 52%, #111827 52% 60%, #020617 60% 100%);
    box-shadow: 0 22px 50px rgba(15, 23, 42, 0.22);
}

.listener-playing__vinyl.is-spinning {
    animation: listener-vinyl-spin 8s linear infinite;
}

.listener-playing__art-card {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
    padding: 0.8rem;
    border-radius: 2rem;
    background: linear-gradient(180deg, rgba(255,255,255,0.72), rgba(255,255,255,0.36));
    backdrop-filter: blur(20px);
    box-shadow: 0 28px 70px rgba(99, 102, 241, 0.18);
}

.listener-playing__art-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1.45rem;
}

.listener-playing__meta {
    text-align: left;
    transition: margin 0.24s ease;
}

.listener-playing__title {
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-playing__artist {
    margin-top: 0.65rem;
    font-size: 1rem;
    color: #94a3b8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-playing__lyrics-stage {
    position: relative;
    margin: 1.25rem 0 1.35rem;
    height: var(--display-stage-size);
    border-radius: 2rem;
    background: transparent;
    overflow: hidden;
    flex-shrink: 0;
}

.listener-playing__lyrics-scroll {
    position: relative;
    z-index: 1;
    min-height: 0;
    height: 100%;
    padding: 1.15rem 0.2rem;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-behavior: smooth;
}

.listener-playing__lyrics-spacer {
    height: 28%;
    min-height: 4.5rem;
}

.listener-playing__lyrics-fade {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 2;
    height: 3.4rem;
    pointer-events: none;
}

.listener-playing__lyrics-fade--top {
    top: 0;
    background: linear-gradient(180deg, rgba(249, 250, 251, 0), rgba(249, 250, 251, 0));
}

.listener-playing__lyrics-fade--bottom {
    bottom: 0;
    background: linear-gradient(0deg, rgba(249, 250, 251, 0), rgba(249, 250, 251, 0));
}

.listener-playing__progress-block {
    margin-top: 1.35rem;
    transition: margin 0.24s ease;
}

.listener-playing__progress-curve {
    width: 100%;
    height: 3rem;
    outline: none;
    touch-action: none;
}

.listener-playing__progress-svg {
    width: 100%;
    height: 100%;
    overflow: visible;
}

.listener-playing__progress-track,
.listener-playing__progress-fill {
    fill: none;
    stroke-linecap: round;
    stroke-width: 4.25;
}

.listener-playing__progress-track {
    stroke: rgba(203, 213, 225, 0.55);
    filter: drop-shadow(0 4px 12px rgba(148, 163, 184, 0.12));
}

.listener-playing__progress-fill {
    stroke: #111827;
    transition: stroke-dasharray 0.16s ease;
}

.listener-playing__progress-thumb {
    fill: #111827;
    stroke: rgba(255, 255, 255, 0.92);
    stroke-width: 2;
    filter: drop-shadow(0 6px 12px rgba(15, 23, 42, 0.22));
}

.listener-playing__time {
    margin-top: 0.55rem;
    display: flex;
    justify-content: space-between;
    font-size: 0.78rem;
    color: #94a3b8;
}

.listener-playing__lyric-line {
    width: 100%;
    padding: 0.92rem 1rem;
    border: 0;
    border-radius: 1rem;
    background: transparent;
    color: #94a3b8;
    text-align: center;
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.7;
    word-break: break-word;
    opacity: 0.3;
    transform: scale(0.9);
    transform-origin: center center;
    transition: color 0.2s ease, transform 0.24s ease, opacity 0.24s ease, font-size 0.24s ease;
}

.listener-playing__lyric-line.is-active {
    color: #111827;
    font-size: 1.18rem;
    font-weight: 800;
    opacity: 1;
    transform: scale(1.08);
}

.listener-playing__lyric-line.is-near {
    opacity: 0.72;
    transform: scale(0.98);
}

.listener-playing__lyric-line.is-mid {
    opacity: 0.5;
    transform: scale(0.94);
}

.listener-playing__lyric-line.is-far {
    opacity: 0.24;
    transform: scale(0.88);
}

.listener-playing__lyrics-empty {
    padding: 3rem 0;
    text-align: center;
    font-size: 0.88rem;
    color: #94a3b8;
}

.listener-playing__toolbar {
    margin-top: 1.35rem;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    align-items: center;
    gap: 0.45rem;
    transition: margin 0.24s ease;
}

.listener-playing__toolbar-button {
    width: 2.85rem;
    height: 2.85rem;
    margin: 0 auto;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #111827;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.listener-playing__toolbar-button svg,
.listener-playing__play-button svg {
    width: 1.35rem;
    height: 1.35rem;
}

.listener-playing__toolbar-button.is-active {
    color: #ef4444;
}

.listener-playing__play-button {
    width: 4.25rem;
    height: 4.25rem;
    margin: 0 auto;
    border: 0;
    border-radius: 999px;
    background: #111827;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 18px 30px rgba(15, 23, 42, 0.24);
}

@media (max-height: 760px) {
    .listener-playing {
        --display-stage-size: min(15.5rem, 66vw, 29dvh);
        padding-bottom: 0;
    }

    .listener-playing__display-switch {
        margin-top: 0.9rem;
    }

    .listener-playing__art-stage,
    .listener-playing__lyrics-stage {
        margin-top: 1rem;
        margin-bottom: 1rem;
    }

    .listener-playing__title {
        font-size: 1.65rem;
    }

    .listener-playing__artist {
        margin-top: 0.45rem;
    }

    .listener-playing__progress-block,
    .listener-playing__toolbar {
        margin-top: 1rem;
    }

    .listener-playing__toolbar-button {
        width: 2.65rem;
        height: 2.65rem;
    }

    .listener-playing__play-button {
        width: 3.85rem;
        height: 3.85rem;
    }
}

@keyframes listener-vinyl-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

</style>
